import { Auth } from 'aws-amplify';
import { useRef } from 'react';

const SOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL; // your WebSocket URL
export default function useWebSocket(onMessage, onError, onClose, anonGroup, chatLocation) {
  const socketRef = useRef(null);
  const connecting = useRef(false);

  async function connect(onMessage, onError, onClose, anonGroup, chatLocation = null, salt = false,
                         uuid = false) {
    if (!connecting.current) {
      try {
        connecting.current = true;
        let token;
        if (anonGroup !== null && anonGroup.length > 0) {
          token = "anonymous";
        }
        else {
          const session = await Auth.currentSession();
          token = session.getIdToken().getJwtToken();
        }
        if (!salt) {
          salt = sessionStorage.getItem('salt');
        }
        if (!uuid) {
          uuid = sessionStorage.getItem('uuid');
        }
        if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
          console.log('WebSocket connecting');
          socketRef.current = new WebSocket(
              SOCKET_URL +
              '?token=' + encodeURIComponent(token) +
              (salt ? '&salt=' + encodeURIComponent(salt) : '') +
              (uuid ? '&uuid=' + encodeURIComponent(uuid) : '') +
              (anonGroup !== null && anonGroup.length > 0 ? '&anon_group=' + encodeURIComponent(anonGroup) : '') +
              (chatLocation !== null && chatLocation.length > 0 ? '&location=' + encodeURIComponent(chatLocation) : ''),
          );

          socketRef.current.onopen = () => {
            console.log('WebSocket connected');
          };

          socketRef.current.onmessage = event => {
            const message = JSON.parse(event.data);
            onMessage(message);
          };

          socketRef.current.onerror = error => {
            if (socketRef.current === error.target) {
              console.error(`WebSocket error (current connection): ${JSON.stringify(error)}`);

              // if we get an error on our current connection, notify
              onError(error);
            } else {
              console.error(`WebSocket error (prior connection): ${JSON.stringify(error)}`);
            }
          };

          socketRef.current.onclose = async event => {
            if (socketRef.current === event.target) {
              socketRef.current = null;
              console.log('WebSocket closed (current connection)');

              if (event.reason === 'Invalid salt or uuid') {
                await connect(onMessage, onError, onClose, anonGroup, chatLocation);
              }

              // important to call this last (might retry a message send)
              await onClose(event);
            }
            else {
              console.log('WebSocket closed (prior connection)');
              // (don't need to do anything about close notifications about prior connections)
            }
          };
        }
      } finally {
        connecting.current = false;
      }
    }
  }

  const DEFAULT_RETRY_DELAY = 125;
  const MAX_RETRY_DELAY = 60000;
  let retryDelay = DEFAULT_RETRY_DELAY; // initial automatic retry delay

  const sendMessage = async message => {
    // if we don't have an open websocket, connect now
    if (!socketRef.current) {
      await connect(onMessage, onError, onClose, anonGroup, chatLocation);
    }

    if (socketRef.current.readyState === WebSocket.OPEN) {
      try {
        socketRef.current.send(JSON.stringify(message));
        retryDelay = DEFAULT_RETRY_DELAY; // reset to default retry delay after successful send
      } catch (error) {
        // if we fail (which we might for a stale connection), show error and retry after delay
        console.error(`Error sending message: ${error}`);
        setTimeout(() => {
          sendMessage(message);
        }, retryDelay);
        retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY); // double delay for next retry, up to max delay
      }
    } else {
      // if the connection isn't in a ready state, retry after delay
      console.log('WebSocket not ready');
      setTimeout(() => {
        sendMessage(message);
      }, retryDelay);
      retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY); // double delay for next retry, up to max delay
    }
  };

  const reconnect = async (salt = false, uuid = false) => {
    console.log('Request to reconnect');
    if (socketRef.current) {
      socketRef.current.close();
    }
    await connect(onMessage, onError, onClose, anonGroup, chatLocation, salt, uuid);
  };

  return { sendMessage, reconnect };
}
